import React from "react"
import endOfDay from "date-fns/endOfDay"
import startOfDay from "date-fns/startOfDay"
import { useHistory, useLocation } from "react-router-dom"
import format from "date-fns/format"

export const useDateRange = () => {
  const history = useHistory()
  const location = useLocation()

  const { fromDate, toDate } = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const today = new Date()

    return {
      fromDate: parseDateQueryParam(searchParams.get("fromDate"), startOfDay(today), today),
      toDate: parseDateQueryParam(searchParams.get("toDate"), endOfDay(today), today),
    }
  }, [location.search])

  const updateDateRangeQuery = React.useCallback(
    ({ fromDate, toDate }: Partial<DateRange>) => {
      const nextSearchParams = new URLSearchParams(location.search)

      if (fromDate) nextSearchParams.set("fromDate", formatDateRangeQuery(clampFutureDate(fromDate)))
      if (toDate) nextSearchParams.set("toDate", formatDateRangeQuery(clampFutureDate(toDate)))

      history.push({ search: nextSearchParams.toString() })
    },
    [history, location.search]
  )

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const expectedFromDate = formatDateRangeQuery(fromDate)
    const expectedToDate = formatDateRangeQuery(toDate)
    let shouldReplace = false

    if (searchParams.get("fromDate") !== expectedFromDate) {
      searchParams.set("fromDate", expectedFromDate)
      shouldReplace = true
    }

    if (searchParams.get("toDate") !== expectedToDate) {
      searchParams.set("toDate", expectedToDate)
      shouldReplace = true
    }

    if (shouldReplace) history.replace({ search: searchParams.toString() })
  }, [fromDate, history, location.search, toDate])

  return {
    fromDate,
    toDate,
    setFromDate: (date: Date) => updateDateRangeQuery({ fromDate: date }),
    setToDate: (date: Date) => updateDateRangeQuery({ toDate: date }),
    setDateRange: updateDateRangeQuery,
  }
}

const parseDateQueryParam = (value: string | null, fallback: Date, maxDate: Date) => {
  if (!value) return fallback

  const date = new Date(value)
  return isNaN(date.getTime()) ? fallback : clampFutureDate(date, maxDate)
}

export const formatDateRangeQuery = (date: Date) => {
  return format(date, "yyyy-MM-dd")
}

const clampFutureDate = (date: Date, maxDate = new Date()) => {
  return date > maxDate ? maxDate : date
}

type DateRange = {
  fromDate: Date
  toDate: Date
}
